/**
 * useSeo — per-route SEO metadata for our Vite SPA.
 *
 * Each route's HTML is prerendered at build time with the right head tags
 * (scripts/prerender-seo.js), so crawlers get correct metadata without
 * running any JavaScript. This hook covers what a static file can't:
 * client-side navigation, where the document never reloads, and dynamic
 * routes like /group/<id> whose canonical URL isn't known until runtime.
 *
 * It mutates document.head on every route change. We don't restore previous
 * values on unmount because the next route always sets its own — restoring
 * would just create flicker.
 *
 * Copy lives in src/seo/routes.js, shared with the prerender so the two can't
 * drift.
 *
 * Usage:
 *   useSeo({
 *     title: "...",
 *     description: "...",
 *     path: "/about",                    // canonical path, no trailing slash unless /
 *     image: "/og-image.png",             // optional, full or absolute URL
 *     jsonLd: [{...}],                    // optional array of structured-data objects
 *   });
 */

import { useEffect } from 'react';

import { SITE_ORIGIN, DEFAULT_OG_IMAGE, JSON_LD_MARKER } from '../seo/routes';

function setMeta(attr, value, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setLink(rel, href) {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

function applyJsonLd(blocks) {
  // Wipe any route-scoped JSON-LD (identified by data-seo-jsonld) so
  // navigating between routes doesn't pile them up. The prerender stamps the
  // same marker, so the blocks baked into the served HTML get replaced here
  // rather than duplicated. Unmarked blocks are sitewide — leave them alone.
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${JSON_LD_MARKER}]`)
    .forEach((node) => node.remove());

  if (!blocks || blocks.length === 0) return;
  for (const block of blocks) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(JSON_LD_MARKER, '');
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  }
}

/**
 * Componentised wrapper so SEO declarations can live inline inside JSX
 * without each consumer needing to wire its own useEffect. Renders nothing.
 */
export function SeoHelmet(props) {
  useSeo(props);
  return null;
}

export default function useSeo({ title, description, path, image, jsonLd } = {}) {
  useEffect(() => {
    if (title) document.title = title;

    const url = path
      ? `${SITE_ORIGIN}${path === '/' ? '/' : path.replace(/\/$/, '')}`
      : window.location.href;
    const ogImage = image || DEFAULT_OG_IMAGE;

    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:image', ogImage);
    setLink('canonical', url);

    applyJsonLd(jsonLd);
  }, [title, description, path, image, JSON.stringify(jsonLd)]);
}
